import {
	ReactElement,
	useMemo,
	useState
} from "react";
import {
	Anchor,
	ButtonBase,
	FormattedText,
	HeightAnimationContainer,
	Icon
} from "client/Components";
import {
	AttendeeModel,
	AttendingStatus,
	OrganizerModel
} from "client/Api/Calendar/calendar";
import { AppointmentAttendeeListitem } from "./AppointmentAttendeeListItem";
import { localize } from "client_legacy/ndaw/culture";
import { groupBy } from "client_legacy/ndaw/arrayHelpers";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import styles from "./AppointmentAttendeesList.module.less";
import { Guid, newGuid } from "client_legacy/ndaw/guid";

type AppointmentAttendeesListProps = {
	variant: "compact" | "full";
	attendees: readonly AttendeeModel[];
	organizer: OrganizerModel | nil;
};

export const AppointmentAttendeesList = (props: AppointmentAttendeesListProps): ReactElement => {
	const {
		attendees,
		organizer,
		variant
	} = props;

	const orderedGroupedAttendees = useMemo(
		(): [AttendingStatus, AttendeeModel[]][] => {
			const groupedAttendees: Partial<Record<AttendingStatus, AttendeeModel[]>> = groupBy(
				attendees,
				x => x.status
			);

			const orderedGroupedAttendees: [AttendingStatus, AttendeeModel[]][] = [];

			for (const attendeeStatus of ATTENDEES_STATUS_ORDER) {
				if (isNotNil(groupedAttendees[attendeeStatus])) {
					orderedGroupedAttendees.push([
						attendeeStatus,
						groupedAttendees[attendeeStatus]
					]);
				}
			}

			return orderedGroupedAttendees;
		},
		[attendees]
	);

	return (
		<ul>
			{organizer && (
				<AppointmentAttendeeListitem
					trackingId={generateTrackingId("appointment-attendee-list-item", `|${organizer.emailAddress}`)}
					displayName={organizer.displayName}
					emailAddress={organizer.emailAddress}
					isOrganizer
				/>
			)}
			<li>
				{variant === "full"
					? (
						orderedGroupedAttendees.map(
							(x: [AttendingStatus, AttendeeModel[]]): ReactElement => {
								const [status, attendees] = x;

								return (
									<AttendeeExpandableSection
										key={status}
										trackingId={generateTrackingId(
											"attendee-expandable-section",
											`|${status}`
										)}
										status={status}
										attendees={attendees}

									/>
								);
							}
						)
					)
					: (
						<AllAttendeesList
							orderedAttendeesByStatus={orderedGroupedAttendees}
							shouldCountOrganizer={isNotNil(organizer)}
						/>
					)
				}
			</li>
		</ul>
	);
};

AppointmentAttendeesList.displayName = "AppointmentAttendeesList";

type AttendeeExpandableSectionProps = {
	trackingId: string;
	attendees: readonly AttendeeModel[];
	status: AttendingStatus;
};

const AttendeeExpandableSection = (
	props: AttendeeExpandableSectionProps
): ReactElement => {
	const {
		trackingId,
		attendees,
		status
	} = props;
	const [isExpanded, setIsExpanded] = useState<boolean>(true);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.attendeeExpandableSection}
		>
			<ButtonBase
				onClick={() => setIsExpanded(!isExpanded)}
				trackingId="expand-button"
				className={styles.expandButton}
			>
				<span className={styles.expandButtonText}>
					<FormattedText
						text={localize("_StatusAmountFormat")}
						thingsToReplace={
							[
								{
									what: "{{Status}}",
									filler: localize(attendingStatusToSectionTitle[status])
								},
								{
									what: "{{Amount}}",
									filler: attendees.length
								}
							]
						}
					/>
				</span>
				<span className={styles.expandButtonIcon}>
					<Icon value={isExpanded ? "chevron-up" : "chevron-down"}/>
				</span>
			</ButtonBase>
			<HeightAnimationContainer isVisible={isExpanded}>
				<ul>
					{attendees.map((x, i) => (
						<AppointmentAttendeeListitem
							trackingId={generateTrackingId("appointment-attendee-list-item", `|${x.emailAddress}`)}
							key={i}
							displayName={x.displayName}
							emailAddress={x.emailAddress}
							status={x.status}
						/>
					))}
				</ul>
			</HeightAnimationContainer>
		</div>
	);

};
AttendeeExpandableSection.displayName = "AttendeeExpandableSection";

type AllAttendeesListProps = {
	orderedAttendeesByStatus: readonly [AttendingStatus, AttendeeModel[]][];
	shouldCountOrganizer: boolean;
};

const AllAttendeesList = (props: AllAttendeesListProps) => {
	const {
		orderedAttendeesByStatus,
		shouldCountOrganizer
	} = props;

	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	const attendees = useMemo(
		(): readonly AttendeeModel[] => {
			return orderedAttendeesByStatus.reduce(
				(
					acc: readonly AttendeeModel[],
					curr: [AttendingStatus, AttendeeModel[]]
				): readonly AttendeeModel[] => [...acc, ...curr[1]],
				[]
			);
		},
		[orderedAttendeesByStatus]
	);

	const attendeesToRender = useMemo(
		(): readonly AttendeeModel[] => {
			if (isExpanded) {
				return attendees;
			}

			return attendees.slice(0, MAX_NUMBER_OF_ATTENDEES_TO_SHOW);
		},
		[
			attendees,
			isExpanded
		]
	);

	const attendeesListId = useMemo(
		(): Guid => {
			return newGuid();
		},
		[]
	);

	return (
		<ul
			aria-label={localize("_Attendees")}
			id={attendeesListId}
		>
			{attendeesToRender.map((x, i) => (
				<AppointmentAttendeeListitem
					key={i}
					displayName={x.displayName}
					emailAddress={x.emailAddress}
					status={x.status}
					trackingId={generateTrackingId("appointment-attendee-list-item", `|${x.emailAddress}`)}
				/>
			))}

			{attendees.length > MAX_NUMBER_OF_ATTENDEES_TO_SHOW && (
				<li>
					<Anchor
						onClick={() => setIsExpanded(x => !x)}
						trackingId="expand-link"
						aria-expanded={isExpanded}
						aria-controls={attendeesListId}
						role="button"
					>
						{isExpanded
							? localize("_ViewLessAttendees")
							: (
								<FormattedText
									text={localize("_ViewAllAmountAttendees")}
									thingsToReplace={
										[
											{
												what: "{{Amount}}",
												filler: attendees.length + (shouldCountOrganizer ? 1 : 0)
											}
										]
									}
								/>
							)
						}
					</Anchor>
				</li>
			)}
		</ul>
	);
};
AllAttendeesList.displayName = "AllAttendeesList";

const attendingStatusToSectionTitle: { [key in AttendingStatus]: LocalizationKey; } = {
	[AttendingStatus.Accept]: "_Accepted",
	[AttendingStatus.Decline]: "_Declined",
	[AttendingStatus.Tenative]: "_Tentative",
	[AttendingStatus.Unknown]: "_NotResponded",
};

const ATTENDEES_STATUS_ORDER = [
	AttendingStatus.Accept,
	AttendingStatus.Decline,
	AttendingStatus.Tenative,
	AttendingStatus.Unknown
];

const MAX_NUMBER_OF_ATTENDEES_TO_SHOW = 4;
